/**
 * The trail (ui/DESIGN.md §3.3): every value chosen is a chip under the search field, "customer · Nastolan
 * Lattiamestarit Oy › city · Nastola": the attribute humanised, the value in mono. A chosen value flies here from
 * where it was clicked (lib/fly.ts); removing one shrinks it away; clicking one opens the values lens at that
 * attribute. The trail is the URL (the `step` params); the page owns the steps.
 */
import { Fragment, useLayoutEffect, useRef } from 'react';
import { humanize } from '../lib/format.ts';
import { land, leave } from '../lib/fly.ts';
import { Chip } from '../design/primitives.tsx';
import type { Step } from './scope.ts';

export function Trail({ steps, onRemove, onOpen }: { steps: Step[]; onRemove: (i: number) => void; onOpen?: (step: Step) => void }) {
  if (steps.length === 0) return null;
  return (
    <nav aria-label="Trail" className="flex items-center justify-center gap-1.5 flex-wrap max-w-full pb-1">
      {steps.map((st, i) => (
        <Fragment key={st.t}>
          {i > 0 && <span className="text-n6 text-small px-0.5" aria-hidden="true">›</span>}
          <TrailChip st={st} onRemove={() => onRemove(i)} onOpen={onOpen && (() => onOpen(st))} />
        </Fragment>
      ))}
    </nav>
  );
}

function TrailChip({ st, onRemove, onOpen }: { st: Step; onRemove: () => void; onOpen?: () => void }) {
  const ref = useRef<HTMLSpanElement>(null);
  // The value chosen below lands here
  useLayoutEffect(() => land(st.t, ref.current), []); // eslint-disable-line react-hooks/exhaustive-deps
  const values = st.vs ?? [st.v];
  return (
    <span ref={ref} className="inline-flex max-w-full">
      <Chip onRemove={() => leave(ref.current, onRemove)} onClick={onOpen} className="bg-n2 shadow-node">
        <span className="text-n7 font-normal">{humanize(st.l).toLowerCase()}</span>
        <span className="text-n5"> · </span>
        <span className="text-n10 font-mono">{values.join(' | ')}</span>
      </Chip>
    </span>
  );
}
